import { NAV_ROUTE_MANIFEST } from './nav-route-manifest';
import type { NavRouteDefinition } from './types/nav-types';

export function matchesPathPattern(pathname: string, pattern: string): boolean {
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  }

  return pathname === pattern;
}

export function getExactPathsForRoute(route: NavRouteDefinition): readonly string[] {
  return [route.path, ...(route.aliases ?? [])];
}

export function isNavRouteActive(pathname: string, route: NavRouteDefinition): boolean {
  if (getExactPathsForRoute(route).includes(pathname)) {
    return true;
  }

  return (route.matchPatterns ?? []).some(pattern => matchesPathPattern(pathname, pattern));
}

export function isNavLinkActive(pathname: string, link: string, matchPatterns?: readonly string[]): boolean {
  if (pathname === link) {
    return true;
  }

  return (matchPatterns ?? []).some(pattern => matchesPathPattern(pathname, pattern));
}

export function findBestMatchingNavRoute(pathname: string): NavRouteDefinition | undefined {
  let bestExact: NavRouteDefinition | undefined;
  let bestExactLength = -1;

  for (const route of NAV_ROUTE_MANIFEST) {
    for (const path of getExactPathsForRoute(route)) {
      if (path === pathname && path.length > bestExactLength) {
        bestExact = route;
        bestExactLength = path.length;
      }
    }
  }

  if (bestExact) {
    return bestExact;
  }

  let bestPattern: NavRouteDefinition | undefined;
  let bestPatternLength = -1;

  for (const route of NAV_ROUTE_MANIFEST) {
    for (const pattern of route.matchPatterns ?? []) {
      if (!matchesPathPattern(pathname, pattern)) {
        continue;
      }
      const stemLength = pattern.endsWith('/*') ? pattern.length - 2 : pattern.length;
      if (stemLength > bestPatternLength) {
        bestPattern = route;
        bestPatternLength = stemLength;
      }
    }
  }

  return bestPattern;
}

export function isAnyNavLinkActive(
  pathname: string,
  items: ReadonlyArray<{
    link?: string;
    matchPatterns?: readonly string[];
    children?: ReadonlyArray<{ link?: string }>;
  }>
): boolean {
  return items.some(item => {
    if (item.link && isNavLinkActive(pathname, item.link, item.matchPatterns)) {
      return true;
    }
    return item.children?.some(child => child.link === pathname) ?? false;
  });
}
